import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBackIos,
  ArrowForwardIos,
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircle,
  Close,
  Delete,
  Description as FileIcon,
  Download,
  GridView,
  InsertPhoto,
  Refresh,
  Schedule,
  Search,
  Sort,
  ViewList,
  Warning,
  ZoomIn,
} from '@mui/icons-material';
import { AppButton, AppIconButton, AppTextField, CompactFilterIconButton } from '../design-system';
import { getAccentSurfaceColors, getMediaScrimColors } from '../../theme/uiSemanticColors';
import { tempFilesService } from '../../services/TempFilesService';
import { downloadTrackedFile } from '../../utils/filePreview';
import type {
  ExpirationStatus,
  TempFileListQuery,
  TempFileMetadata,
  TempFileSortBy,
  TempFileSource,
} from '@cnapse/shared/temp-file-schemas.js';
import {
  formatTempFileSize,
  handleTempFileImageError,
  isTempFileImage,
  tempFileExpirationColor,
  tempFileMatchesMediaKind,
  tempFileFullPreviewUrl,
  tempFilePreviewUrl,
  tempFileSourceLabel,
  type TempFileMediaKind,
} from './tempFileBrowserUtils';

export interface TempFileBrowserProps {
  mediaKind?: TempFileMediaKind;
  /** Enables pick/highlight UX in grid and list. */
  selectionMode?: boolean;
  /** When true, toggles staged selection instead of calling onFileSelect immediately. */
  deferredSelection?: boolean;
  stagedFileIds?: number[];
  onToggleStage?: (file: TempFileMetadata) => void;
  maxSelection?: number;
  onFileSelect?: (fileId: number, previewUrl: string, file: TempFileMetadata) => void;
  gridCols?: number;
  /** Grid tile height in pixels (gallery view). */
  gridTileHeight?: number;
  /** List row thumbnail size in pixels. */
  listThumbnailSize?: number;
  listMaxHeight?: string | number;
  pageSize?: number;
  /** Flatter chrome when nested inside a dialog (no extra boxes/borders). */
  embedded?: boolean;
  /** Gallery page: delete/download actions and extra filters. */
  managementMode?: boolean;
  uploadedByUsers?: Array<{ id: number; name: string }>;
  onMutation?: () => void;
  /** Increment to trigger a data refresh from the parent. */
  refreshToken?: number;
  /** Called when the visible file list changes (e.g. for bulk actions in gallery mode). */
  onFilesChange?: (files: TempFileMetadata[]) => void;
  /** Selected file IDs for gallery bulk actions. */
  selectedFileIds?: number[];
  onSelectedFileIdsChange?: (ids: number[]) => void;
  /** Upload / download / delete actions rendered in the management toolbar row. */
  managementActions?: React.ReactNode;
  /** Optional stats or context shown before the pagination caption (e.g. inbox totals). */
  summaryPrefix?: string;
  /** Called when the file list loading state changes. */
  onLoadingChange?: (loading: boolean) => void;
}

export const TempFileBrowser: React.FC<TempFileBrowserProps> = ({
  mediaKind = 'any',
  selectionMode = false,
  deferredSelection = false,
  stagedFileIds = [],
  onToggleStage,
  maxSelection,
  onFileSelect,
  gridCols = 5,
  gridTileHeight = 200,
  listThumbnailSize = 72,
  listMaxHeight = 'min(52vh, 520px)',
  pageSize = 50,
  embedded = false,
  managementMode = false,
  uploadedByUsers = [],
  onMutation,
  refreshToken = 0,
  onFilesChange,
  selectedFileIds = [],
  onSelectedFileIdsChange,
  managementActions,
  summaryPrefix,
  onLoadingChange,
}) => {
  const theme = useTheme();
  const accentSurfaces = useMemo(() => getAccentSurfaceColors(theme), [theme]);
  const mediaScrim = useMemo(() => getMediaScrimColors(theme), [theme]);

  const [files, setFiles] = useState<TempFileMetadata[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize,
    totalItems: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [previewFile, setPreviewFile] = useState<TempFileMetadata | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<Partial<TempFileListQuery>>({
    page: 1,
    pageSize,
    sortBy: 'uploadedAt',
    sortOrder: 'desc',
    includeMetadata: true,
    search: '',
    mediaKind,
  });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, mediaKind, page: 1 }));
  }, [mediaKind]);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tempFilesService.listTempFiles({
        ...filters,
        mediaKind,
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? pageSize,
      } as TempFileListQuery);
      if (response.success && response.data) {
        const visibleFiles = response.data.files.filter((file) =>
          tempFileMatchesMediaKind(file, mediaKind),
        );
        setFiles(visibleFiles);
        onFilesChange?.(visibleFiles);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      console.error('Failed to load temp files:', err);
      setError('Failed to load temp files');
    } finally {
      setLoading(false);
    }
  }, [filters, mediaKind, pageSize, onFilesChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchFiles();
    }, filters.search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchFiles, filters.search]);

  useEffect(() => {
    if (refreshToken > 0) {
      void fetchFiles();
    }
  }, [refreshToken, fetchFiles]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handlePageChange = (nextPage: number) => {
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  const openPreview = (file: TempFileMetadata) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const bulkSelectEnabled = managementMode && !selectionMode && Boolean(onSelectedFileIdsChange);
  const isStaged = (fileId: number) => stagedFileIds.includes(fileId);
  const isBulkSelected = (fileId: number) => selectedFileIds.includes(fileId);
  const isHighlighted = (fileId: number) => isStaged(fileId) || isBulkSelected(fileId);
  const showSelectionChrome = (fileId: number) => isHighlighted(fileId) && !bulkSelectEnabled;

  const toggleBulkSelect = (fileId: number) => {
    if (!onSelectedFileIdsChange) return;
    if (isBulkSelected(fileId)) {
      onSelectedFileIdsChange(selectedFileIds.filter((id) => id !== fileId));
    } else {
      onSelectedFileIdsChange([...selectedFileIds, fileId]);
    }
  };

  const selectAllOnPage = () => {
    if (!onSelectedFileIdsChange) return;
    const merged = new Set([...selectedFileIds, ...files.map((f) => f.id)]);
    onSelectedFileIdsChange([...merged]);
  };

  const clearBulkSelection = () => onSelectedFileIdsChange?.([]);

  const handlePickAction = (file: TempFileMetadata) => {
    if (!tempFileMatchesMediaKind(file, mediaKind)) return;
    if (deferredSelection && onToggleStage) {
      onToggleStage(file);
      return;
    }
    const previewUrl = tempFilePreviewUrl(file) ?? '';
    onFileSelect?.(file.id, previewUrl, file);
  };

  const selectionAtCapacity =
    maxSelection != null && stagedFileIds.length >= maxSelection;

  const handleDownloadFile = async (file: TempFileMetadata) => {
    try {
      await downloadTrackedFile(
        { objectKey: file.objectKey },
        file.originalName || `temp-file-${file.id}`,
      );
    } catch (err) {
      console.error('Failed to download temp file:', err);
      setError('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    if (!window.confirm('Delete this temp file?')) return;
    try {
      await tempFilesService.deleteTempFile(fileId);
      if (previewFile?.id === fileId) {
        setPreviewOpen(false);
        setPreviewFile(null);
      }
      await fetchFiles();
      onMutation?.();
    } catch (err) {
      console.error('Failed to delete temp file:', err);
      setError('Failed to delete file');
    }
  };

  const navigatePreview = (direction: 'prev' | 'next') => {
    if (!previewFile) return;
    const index = files.findIndex((f) => f.id === previewFile.id);
    const nextIndex = direction === 'next' ? index + 1 : index - 1;
    if (nextIndex >= 0 && nextIndex < files.length) {
      setPreviewFile(files[nextIndex]);
    }
  };

  const renderFileThumbnail = (file: TempFileMetadata, size: number | string) => {
    const previewUrl = tempFilePreviewUrl(file);
    const isImage = isTempFileImage(file);
    if (isImage && previewUrl) {
      return (
        <Box
          component="img"
          src={previewUrl}
          alt={file.originalName}
          loading="lazy"
          onError={(e) => handleTempFileImageError(file, e)}
          sx={{ width: size, height: size, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
        />
      );
    }
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          flexShrink: 0,
        }}
      >
        {isImage ? <InsertPhoto color="disabled" /> : <FileIcon color="disabled" />}
      </Box>
    );
  };

  const renderThumbnailMetaLine = (file: TempFileMetadata) => {
    const expirationTone = tempFileExpirationColor(file.daysUntilExpiration);
    return (
      <Typography
        variant="caption"
        noWrap
        sx={{
          display: 'block',
          mt: 0.25,
          color: alpha(theme.palette.common.white, 0.78),
          fontSize: '0.65rem',
          lineHeight: 1.2,
        }}
      >
        {tempFileSourceLabel(file.metadata.source)}
        {' · '}
        <Box component="span" sx={{ color: `${expirationTone}.light`, fontWeight: 600 }}>
          {file.daysUntilExpiration}d
        </Box>
      </Typography>
    );
  };

  const renderThumbnailFooter = (file: TempFileMetadata, action: React.ReactNode) => (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: mediaScrim.footer,
        px: 1,
        py: 0.75,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          noWrap
          sx={{ display: 'block', color: 'common.white', fontWeight: 600, lineHeight: 1.3 }}
        >
          {file.originalName}
        </Typography>
        {renderThumbnailMetaLine(file)}
      </Box>
      {action}
    </Box>
  );

  const filterControlSx = embedded
    ? { minWidth: 110, overflow: 'visible' as const }
    : { minWidth: 110 };

  const useCompactManagementToolbar = managementMode && !embedded;

  const hasActiveFilters = Boolean(
    filters.source || filters.expirationStatus || filters.uploadedByUserId,
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.source) count += 1;
    if (filters.expirationStatus) count += 1;
    if (filters.uploadedByUserId) count += 1;
    return count;
  }, [filters.source, filters.expirationStatus, filters.uploadedByUserId]);

  const sortSummary = useMemo(() => {
    const sortLabels: Record<TempFileSortBy, string> = {
      uploadedAt: 'Uploaded',
      expiresAt: 'Expires',
      originalName: 'Name',
      sizeBytes: 'Size',
      blurScore: 'Quality',
      uploadedByUser: 'Uploader',
    };
    const by = sortLabels[filters.sortBy ?? 'uploadedAt'];
    const order = filters.sortOrder === 'asc' ? 'Oldest' : 'Newest';
    return `${by} · ${order}`;
  }, [filters.sortBy, filters.sortOrder]);

  const renderGridSelectControl = (fileId: number) => {
    const selected = isBulkSelected(fileId);
    const iconShadow = 'drop-shadow(0 1px 2px rgba(0,0,0,0.75))';
    return (
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleBulkSelect(fileId)}
        size="small"
        disableRipple
        icon={<CheckBoxOutlineBlank sx={{ fontSize: 22, filter: iconShadow }} />}
        checkedIcon={<CheckBox sx={{ fontSize: 22, filter: iconShadow }} />}
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          zIndex: 10,
          p: 0.25,
          m: 0,
          bgcolor: 'transparent',
          color: alpha(theme.palette.common.white, 0.95),
          '&.Mui-checked': {
            color: 'primary.main',
          },
          '&:hover': {
            bgcolor: alpha(theme.palette.common.black, 0.18),
          },
        }}
      />
    );
  };

  const paginationCaption = loading
    ? 'Loading…'
    : `${pagination.totalItems} file${pagination.totalItems === 1 ? '' : 's'} · page ${pagination.page} of ${pagination.totalPages}`;

  const footerCaption = useMemo(() => {
    const parts: string[] = [];
    if (summaryPrefix) parts.push(summaryPrefix);
    parts.push(paginationCaption);
    if (deferredSelection && stagedFileIds.length > 0) {
      parts.push(`${stagedFileIds.length} selected`);
    }
    if (bulkSelectEnabled && selectedFileIds.length > 0) {
      parts.push(`${selectedFileIds.length} selected`);
    }
    return parts.join(' · ');
  }, [
    bulkSelectEnabled,
    deferredSelection,
    paginationCaption,
    selectedFileIds.length,
    stagedFileIds.length,
    summaryPrefix,
  ]);

  const legacyToolbar = (
        <Stack spacing={1} sx={embedded ? { overflow: 'visible', pt: 0.5 } : undefined}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={embedded ? { minHeight: 52, overflow: 'visible' } : undefined}
          >
            <AppTextField
              placeholder={
                managementMode
                  ? 'Search filename, wafer ID, job ID, or uploader…'
                  : 'Search…'
              }
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: '1 1 200px',
                minWidth: 180,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: accentSurfaces.rowHoverBg,
                },
              }}
            />
            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.source || ''}
                label="Source"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    source: e.target.value ? (e.target.value as TempFileSource) : undefined,
                    page: 1,
                  })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="container_slot_scan">Slot scans</MenuItem>
                <MenuItem value="manual_upload">Manual</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={filterControlSx}>
              <InputLabel>Expires</InputLabel>
              <Select
                value={filters.expirationStatus || ''}
                label="Expires"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    expirationStatus: e.target.value ? (e.target.value as ExpirationStatus) : undefined,
                    page: 1,
                  })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="expiring_soon">Soon</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ ...filterControlSx, minWidth: 100 }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={filters.sortBy || 'uploadedAt'}
                label="Sort"
                onChange={(e) =>
                  setFilters({ ...filters, sortBy: e.target.value as TempFileSortBy, page: 1 })
                }
              >
                <MenuItem value="uploadedAt">Uploaded</MenuItem>
                <MenuItem value="expiresAt">Expires</MenuItem>
                <MenuItem value="originalName">Name</MenuItem>
                <MenuItem value="sizeBytes">Size</MenuItem>
                <MenuItem value="blurScore">Quality</MenuItem>
                <MenuItem value="uploadedByUser">Uploader</MenuItem>
              </Select>
            </FormControl>

            {managementMode && uploadedByUsers.length > 0 ? (
              <FormControl size="small" sx={{ ...filterControlSx, minWidth: 160 }}>
                <InputLabel>Uploaded by</InputLabel>
                <Select
                  value={filters.uploadedByUserId ?? ''}
                  label="Uploaded by"
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      uploadedByUserId: e.target.value ? Number(e.target.value) : undefined,
                      page: 1,
                    })
                  }
                >
                  <MenuItem value="">All users</MenuItem>
                  {uploadedByUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            <FormControl size="small" sx={{ ...filterControlSx, minWidth: 96 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder || 'desc'}
                label="Order"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    sortOrder: e.target.value as 'asc' | 'desc',
                    page: 1,
                  })
                }
              >
                <MenuItem value="desc">Newest</MenuItem>
                <MenuItem value="asc">Oldest</MenuItem>
              </Select>
            </FormControl>

            <AppIconButton onClick={() => void fetchFiles()} aria-label="Refresh" disabled={loading}>
              <Refresh />
            </AppIconButton>

            {bulkSelectEnabled ? (
              <>
                <AppButton size="small" variant="text" onClick={selectAllOnPage} disabled={files.length === 0}>
                  Select page
                </AppButton>
                <AppButton
                  size="small"
                  variant="text"
                  onClick={clearBulkSelection}
                  disabled={selectedFileIds.length === 0}
                >
                  Clear
                </AppButton>
              </>
            ) : null}

            <AppIconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              aria-label="Thumbnail view"
            >
              <GridView />
            </AppIconButton>
            <AppIconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              aria-label="List view"
            >
              <ViewList />
            </AppIconButton>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {footerCaption}
          </Typography>
        </Stack>
  );

  const managementToolbar = (
    <Stack spacing={1} sx={{ flexShrink: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <AppTextField
          placeholder="Search filename, wafer ID, job ID, or uploader…"
          value={filters.search || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 220px', minWidth: 200, maxWidth: 480 }}
        />
        <CompactFilterIconButton
          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
          activeCount={activeFilterCount}
          open={Boolean(filterAnchorEl)}
        />
        <Tooltip title={`Sort: ${sortSummary}`}>
          <span>
            <AppIconButton
              size="small"
              onClick={(e) => setSortAnchorEl(e.currentTarget)}
              color={sortAnchorEl ? 'primary' : 'default'}
              aria-label={`Sort: ${sortSummary}`}
            >
              <Sort fontSize="small" />
            </AppIconButton>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1, minWidth: 8 }} />

        {managementActions}

        {bulkSelectEnabled ? (
          <>
            <AppButton size="small" variant="text" onClick={selectAllOnPage} disabled={files.length === 0}>
              Select page
            </AppButton>
            <AppButton
              size="small"
              variant="text"
              onClick={clearBulkSelection}
              disabled={selectedFileIds.length === 0}
            >
              Clear
            </AppButton>
          </>
        ) : null}

        <AppIconButton
          onClick={() => setViewMode('grid')}
          color={viewMode === 'grid' ? 'primary' : 'default'}
          aria-label="Thumbnail view"
        >
          <GridView />
        </AppIconButton>
        <AppIconButton
          onClick={() => setViewMode('list')}
          color={viewMode === 'list' ? 'primary' : 'default'}
          aria-label="List view"
        >
          <ViewList />
        </AppIconButton>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {footerCaption}
      </Typography>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 280 } } }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2">Filters</Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={filters.source || ''}
              label="Source"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  source: e.target.value ? (e.target.value as TempFileSource) : undefined,
                  page: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="container_slot_scan">Slot scans</MenuItem>
              <MenuItem value="manual_upload">Manual</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Expires</InputLabel>
            <Select
              value={filters.expirationStatus || ''}
              label="Expires"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  expirationStatus: e.target.value ? (e.target.value as ExpirationStatus) : undefined,
                  page: 1,
                })
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="expiring_soon">Soon</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          {uploadedByUsers.length > 0 ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Uploaded by</InputLabel>
              <Select
                value={filters.uploadedByUserId ?? ''}
                label="Uploaded by"
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    uploadedByUserId: e.target.value ? Number(e.target.value) : undefined,
                    page: 1,
                  })
                }
              >
                <MenuItem value="">All users</MenuItem>
                {uploadedByUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {hasActiveFilters ? (
            <AppButton
              size="small"
              variant="text"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  source: undefined,
                  expirationStatus: undefined,
                  uploadedByUserId: undefined,
                  page: 1,
                }))
              }
            >
              Clear filters
            </AppButton>
          ) : null}
        </Stack>
      </Popover>

      <Popover
        open={Boolean(sortAnchorEl)}
        anchorEl={sortAnchorEl}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 240 } } }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2">Sort</Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={filters.sortBy || 'uploadedAt'}
              label="Sort by"
              onChange={(e) =>
                setFilters({ ...filters, sortBy: e.target.value as TempFileSortBy, page: 1 })
              }
            >
              <MenuItem value="uploadedAt">Uploaded</MenuItem>
              <MenuItem value="expiresAt">Expires</MenuItem>
              <MenuItem value="originalName">Name</MenuItem>
              <MenuItem value="sizeBytes">Size</MenuItem>
              <MenuItem value="blurScore">Quality</MenuItem>
              <MenuItem value="uploadedByUser">Uploader</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Order</InputLabel>
            <Select
              value={filters.sortOrder || 'desc'}
              label="Order"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  sortOrder: e.target.value as 'asc' | 'desc',
                  page: 1,
                })
              }
            >
              <MenuItem value="desc">Newest first</MenuItem>
              <MenuItem value="asc">Oldest first</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Popover>
    </Stack>
  );

  const toolbar = useCompactManagementToolbar ? managementToolbar : legacyToolbar;

  return (
    <Box>
      {embedded ? (
        <Box sx={{ mb: 1.5, overflow: 'visible' }}>{toolbar}</Box>
      ) : useCompactManagementToolbar ? (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>{toolbar}</Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          {toolbar}
        </Paper>
      )}

      {loading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && files.length === 0 ? (
        <Alert severity="info">
          {filters.search || filters.source || filters.expirationStatus
            ? `No ${mediaKind === 'image' ? 'images' : mediaKind === 'document' ? 'documents' : 'files'} match your filters.`
            : managementMode
              ? 'No temp files yet. Upload manually or import from container slot scans.'
              : mediaKind === 'image'
                ? 'No temp images in the inbox. Upload from Operations → Temp files.'
                : mediaKind === 'document'
                  ? 'No temp documents in the inbox. Upload from Operations → Temp files.'
                  : 'No temp files in the inbox. Upload from Operations → Temp files.'}
        </Alert>
      ) : null}

      {files.length > 0 && viewMode === 'grid' ? (
        <Box sx={{ maxHeight: listMaxHeight, overflow: 'auto', p: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gap: 1.5,
            }}
          >
            {files.map((file) => {
              const previewUrl = tempFilePreviewUrl(file);
              const isImage = isTempFileImage(file);
              const highlighted = showSelectionChrome(file.id);
              return (
                <Box
                  key={file.id}
                  onClick={() => openPreview(file)}
                  sx={{
                    position: 'relative',
                    height: gridTileHeight,
                    cursor: 'pointer',
                    borderRadius: 1,
                    boxShadow: highlighted
                      ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                      : embedded
                        ? 'none'
                        : `inset 0 0 0 1px ${theme.palette.divider}`,
                    '&:hover': embedded
                      ? {}
                      : {
                          boxShadow: highlighted
                            ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                            : `inset 0 0 0 1px ${theme.palette.primary.light}`,
                        },
                  }}
                >
                  {bulkSelectEnabled ? renderGridSelectControl(file.id) : null}
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      bgcolor: isImage ? 'common.black' : 'action.hover',
                      borderRadius: 1,
                      overflow: 'hidden',
                      '&:hover': embedded
                        ? { bgcolor: isImage ? 'common.black' : 'action.selected' }
                        : undefined,
                    }}
                  >
                  {isImage && previewUrl ? (
                    <Box
                      component="img"
                      src={previewUrl}
                      alt={file.originalName}
                      loading="lazy"
                      onError={(e) => handleTempFileImageError(file, e)}
                      sx={{ height: '100%', width: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        p: 1.5,
                      }}
                    >
                      <FileIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="caption" textAlign="center" noWrap sx={{ maxWidth: '100%', px: 1 }}>
                        {file.originalName}
                      </Typography>
                    </Box>
                  )}
                  {renderThumbnailFooter(
                    file,
                    selectionMode ? (
                      <AppIconButton
                        size="small"
                        sx={{ color: isStaged(file.id) ? 'primary.main' : mediaScrim.iconMuted }}
                        disabled={!isStaged(file.id) && selectionAtCapacity}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePickAction(file);
                        }}
                        aria-label={isStaged(file.id) ? 'Deselect file' : 'Select file'}
                      >
                        <CheckCircle />
                      </AppIconButton>
                    ) : (
                      <AppIconButton
                        size="small"
                        sx={{ color: mediaScrim.iconMuted }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(file);
                        }}
                        aria-label="Preview"
                      >
                        <ZoomIn />
                      </AppIconButton>
                    ),
                  )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}

      {files.length > 0 && viewMode === 'list' ? (
        <Stack spacing={1} sx={{ maxHeight: listMaxHeight, overflow: 'auto', p: 0.5 }}>
          {files.map((file) => {
            const highlighted = showSelectionChrome(file.id);
            return (
            <Card
              key={file.id}
              variant={embedded ? 'elevation' : 'outlined'}
              elevation={embedded ? 0 : undefined}
              sx={{
                overflow: 'visible',
                ...(embedded
                  ? {
                      bgcolor: highlighted ? 'action.selected' : 'transparent',
                      boxShadow: highlighted
                        ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                        : 'none',
                      '&:hover': { bgcolor: 'action.hover' },
                    }
                  : {
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                    }),
              }}
            >
              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 }, ...(embedded ? { px: 1 } : {}) }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                    {bulkSelectEnabled ? (
                      <Checkbox
                        checked={isBulkSelected(file.id)}
                        onChange={() => toggleBulkSelect(file.id)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ p: 0.5, ml: -0.5 }}
                      />
                    ) : null}
                    {renderFileThumbnail(file, listThumbnailSize)}
                  </Stack>
                  <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
                    <Typography variant="subtitle2" noWrap title={file.originalName}>
                      {file.originalName}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      <Chip
                        label={tempFileSourceLabel(file.metadata.source)}
                        size="small"
                        variant={embedded ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label={`${file.daysUntilExpiration}d left`}
                        size="small"
                        color={tempFileExpirationColor(file.daysUntilExpiration)}
                        variant={embedded ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label={formatTempFileSize(file.sizeBytes)}
                        size="small"
                        variant={embedded ? 'filled' : 'outlined'}
                      />
                      {file.metadata.blurScore != null ? (
                        <Chip
                          label={`Q ${file.metadata.blurScore}`}
                          size="small"
                          variant={embedded ? 'filled' : 'outlined'}
                        />
                      ) : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {new Date(file.uploadedAt).toLocaleString()}
                      {file.uploadedByUser
                        ? ` · ${file.uploadedByUser.firstName} ${file.uploadedByUser.lastName}`.trim()
                        : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} flexShrink={0} sx={{ alignSelf: 'center' }}>
                    <AppIconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(file);
                      }}
                      aria-label="Preview"
                    >
                      <ZoomIn />
                    </AppIconButton>
                    {managementMode && !selectionMode ? (
                      <>
                        <AppIconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDownloadFile(file);
                          }}
                          aria-label="Download"
                        >
                          <Download fontSize="small" />
                        </AppIconButton>
                        <AppIconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteFile(file.id);
                          }}
                          aria-label="Delete"
                        >
                          <Delete fontSize="small" />
                        </AppIconButton>
                      </>
                    ) : null}
                    {selectionMode ? (
                      <AppButton
                        size="small"
                        variant={isStaged(file.id) ? 'outlined' : 'contained'}
                        disabled={!isStaged(file.id) && selectionAtCapacity}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePickAction(file);
                        }}
                      >
                        {isStaged(file.id) ? 'Selected' : 'Select'}
                      </AppButton>
                    ) : null}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            );
          })}
        </Stack>
      ) : null}

      {pagination.totalPages > 1 ? (
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2 }}>
          <AppButton
            size="small"
            variant="outlined"
            disabled={!pagination.hasPrev || loading}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </AppButton>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages}
          </Typography>
          <AppButton
            size="small"
            variant="outlined"
            disabled={!pagination.hasNext || loading}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </AppButton>
        </Stack>
      ) : null}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        {previewFile ? (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" noWrap sx={{ pr: 2 }}>
                  {previewFile.originalName}
                </Typography>
                <AppIconButton onClick={() => setPreviewOpen(false)} aria-label="Close">
                  <Close />
                </AppIconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers={!embedded}>
              <Box sx={{ position: 'relative', textAlign: 'center', mb: 2, minHeight: 120 }}>
                {files.findIndex((f) => f.id === previewFile.id) > 0 ? (
                  <AppIconButton
                    onClick={() => navigatePreview('prev')}
                    aria-label="Previous file"
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: mediaScrim.control,
                      color: 'white',
                      '&:hover': { bgcolor: mediaScrim.controlHover },
                      zIndex: 1,
                    }}
                  >
                    <ArrowBackIos />
                  </AppIconButton>
                ) : null}
                {isTempFileImage(previewFile) && tempFileFullPreviewUrl(previewFile) ? (
                  <Box
                    component="img"
                    src={tempFileFullPreviewUrl(previewFile)}
                    alt={previewFile.originalName}
                    onError={(e) => handleTempFileImageError(previewFile, e)}
                    sx={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain' }}
                  />
                ) : (
                  <Stack alignItems="center" spacing={1} py={4}>
                    <FileIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                    <Typography color="text.secondary">No image preview</Typography>
                  </Stack>
                )}
                {files.findIndex((f) => f.id === previewFile.id) < files.length - 1 ? (
                  <AppIconButton
                    onClick={() => navigatePreview('next')}
                    aria-label="Next file"
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: mediaScrim.control,
                      color: 'white',
                      '&:hover': { bgcolor: mediaScrim.controlHover },
                      zIndex: 1,
                    }}
                  >
                    <ArrowForwardIos />
                  </AppIconButton>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Source: ${tempFileSourceLabel(previewFile.metadata.source)}`} size="small" />
                <Chip
                  label={`Expires in ${previewFile.daysUntilExpiration}d`}
                  size="small"
                  icon={<Schedule />}
                  color={tempFileExpirationColor(previewFile.daysUntilExpiration)}
                />
                <Chip label={formatTempFileSize(previewFile.sizeBytes)} size="small" variant="outlined" />
                {previewFile.metadata.blurScore != null ? (
                  <Chip
                    label={`Quality ${previewFile.metadata.blurScore}%`}
                    size="small"
                    icon={previewFile.metadata.blurScore >= 50 ? <CheckCircle /> : <Warning />}
                  />
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              {managementMode && !selectionMode ? (
                <>
                  <AppButton onClick={() => void handleDownloadFile(previewFile)} startIcon={<Download />}>
                    Download
                  </AppButton>
                  <AppButton
                    color="error"
                    onClick={() => void handleDeleteFile(previewFile.id)}
                    startIcon={<Delete />}
                  >
                    Delete
                  </AppButton>
                </>
              ) : null}
              <AppButton onClick={() => setPreviewOpen(false)}>Close</AppButton>
              {selectionMode ? (
                <AppButton
                  variant="contained"
                  disabled={!isStaged(previewFile.id) && selectionAtCapacity}
                  onClick={() => {
                    handlePickAction(previewFile);
                    if (!deferredSelection) setPreviewOpen(false);
                  }}
                >
                  {isStaged(previewFile.id) ? 'Remove from selection' : 'Add to selection'}
                </AppButton>
              ) : null}
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      {loading && files.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}
    </Box>
  );
};

export default TempFileBrowser;
